import React, { useState, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { sanitizeString } from '../../utils/sanitization';
import { logEntityChanges } from '../../utils/logger';
import { FEATURE_PERMISSIONS } from '../../utils/permissions';

/**
 * FollowUpCard
 *
 * Shown on the OppDetailPage for "Closed Lost" and "Not Qualified" opportunities.
 * Allows authorized users to flag an opportunity for future re-engagement,
 * set a follow-up date, add notes, and mark the follow-up as complete.
 *
 * Props:
 *   opportunity   {Object}   Full opportunity document from Firestore
 *   oppId         {string}   Opportunity document ID
 *   currentUser   {Object}   Authenticated user from useAuth
 *   can           {Function} Permission checker from usePermissions
 *   onUpdate      {Function} Callback invoked with the applied update map after a save
 */
const FollowUpCard = ({ opportunity, oppId, currentUser, can, onUpdate }) => {
  const canEdit = can(FEATURE_PERMISSIONS.OPP_FOLLOWUP_MANAGE);

  // Local form state — initialized from Firestore data
  const [followUpFlag, setFollowUpFlag] = useState(!!opportunity?.followUpFlag);
  const [followUpDate, setFollowUpDate] = useState(opportunity?.followUpDate || '');
  const [followUpNotes, setFollowUpNotes] = useState(opportunity?.followUpNotes || '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Completion prompt modal state
  const [showCompletePrompt, setShowCompletePrompt] = useState(false);

  // Sync form state if parent's opportunity prop refreshes (e.g. another user edits)
  useEffect(() => {
    setFollowUpFlag(!!opportunity?.followUpFlag);
    setFollowUpDate(opportunity?.followUpDate || '');
    setFollowUpNotes(opportunity?.followUpNotes || '');
  }, [opportunity?.followUpFlag, opportunity?.followUpDate, opportunity?.followUpNotes]);

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    setError('');

    const isNewlyFlagged = followUpFlag && !opportunity?.followUpFlag;
    const isCleared = !followUpFlag && opportunity?.followUpFlag;

    const updates = {
      followUpFlag,
      followUpDate: followUpFlag ? sanitizeString(followUpDate, 20) : '',
      followUpNotes: followUpFlag ? sanitizeString(followUpNotes, 1000) : '',
    };

    const now = new Date();
    if (isNewlyFlagged) {
      updates.followUpSetByUid = currentUser.uid;
      updates.followUpSetByName = currentUser.displayName || currentUser.email;
      updates.followUpSetAt = serverTimestamp();
      updates.followUpCompleted = false;
      updates.followUpCompletedAt = null;
      updates.followUpCompletedByUid = null;
    } else if (isCleared) {
      updates.followUpSetByUid = null;
      updates.followUpSetByName = null;
      updates.followUpSetAt = null;
      updates.followUpCompleted = false;
      updates.followUpCompletedAt = null;
      updates.followUpCompletedByUid = null;
    } else if (followUpFlag) {
      // Existing flag being updated (e.g. date or notes changed) — refresh audit trail
      updates.followUpSetByUid = currentUser.uid;
      updates.followUpSetByName = currentUser.displayName || currentUser.email;
      updates.followUpSetAt = serverTimestamp();
    }

    try {
      await updateDoc(doc(db, collectionPath('opportunities'), oppId), updates);
      await logEntityChanges({
        oldObj: opportunity,
        newObj: { ...opportunity, ...updates },
        fields: ['followUpFlag', 'followUpDate', 'followUpNotes'],
        entityType: 'Opportunity',
        entityId: oppId,
        user: currentUser,
        action: followUpFlag ? 'Set Follow-Up' : 'Cleared Follow-Up',
        collectionName: collectionPath('logsOpp'),
      });
      // Pass real Date for immediate display — serverTimestamp() sentinel can't be formatted
      const localUpdates = (isNewlyFlagged || followUpFlag) ? { ...updates, followUpSetAt: now } : updates;
      if (onUpdate) onUpdate(localUpdates);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!canEdit) return;
    setShowCompletePrompt(true);
  };

  // Handle the user's choice from the completion prompt
  const handleCompleteChoice = async (choice) => {
    setShowCompletePrompt(false);
    setSaving(true);
    setError('');

    if (choice === 're-engage') {
      // Reset follow-up: keep flag, clear completed, update audit trail
      const now = new Date();
      const updates = {
        followUpFlag: true,
        followUpDate: '',
        followUpNotes: '',
        followUpCompleted: false,
        followUpCompletedAt: null,
        followUpCompletedByUid: null,
        followUpSetByUid: currentUser.uid,
        followUpSetByName: currentUser.displayName || currentUser.email,
        followUpSetAt: serverTimestamp(),
      };
      try {
        await updateDoc(doc(db, collectionPath('opportunities'), oppId), updates);
        await logEntityChanges({
          oldObj: opportunity,
          newObj: { ...opportunity, ...updates },
          fields: ['followUpFlag', 'followUpDate', 'followUpNotes'],
          entityType: 'Opportunity',
          entityId: oppId,
          user: currentUser,
          action: 'Re-Engaged Follow-Up',
          collectionName: collectionPath('logsOpp'),
        });
        setFollowUpFlag(true);
        setFollowUpDate('');
        setFollowUpNotes('');
        if (onUpdate) onUpdate({ ...updates, followUpSetAt: now });
      } catch (err) {
        setError(err.message);
      } finally {
        setSaving(false);
      }
      return;
    }

    // choice is 'qualified' or 'closed-lost' — mark complete and move stage
    const newStage = choice === 'qualified' ? 'Qualified' : 'Closed Lost';
    const updates = {
      followUpFlag: false,
      followUpCompleted: true,
      followUpCompletedAt: serverTimestamp(),
      followUpCompletedByUid: currentUser.uid,
      stage: newStage,
    };

    try {
      await updateDoc(doc(db, collectionPath('opportunities'), oppId), updates);
      await logEntityChanges({
        oldObj: opportunity,
        newObj: { ...opportunity, ...updates },
        fields: ['followUpFlag', 'followUpCompleted', 'followUpCompletedAt', 'stage'],
        entityType: 'Opportunity',
        entityId: oppId,
        user: currentUser,
        action: 'Completed Follow-Up',
        collectionName: collectionPath('logsOpp'),
      });
      setFollowUpFlag(false);
      if (onUpdate) onUpdate({ ...updates, followUpCompletedAt: new Date() });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Format a Firestore Timestamp or ISO string for display
  const formatTimestamp = (val) => {
    if (!val) return null;
    try {
      const date = typeof val.toDate === 'function' ? val.toDate() : new Date(val);
      return date.toLocaleDateString();
    } catch {
      return null;
    }
  };

  const setByDisplay = opportunity?.followUpSetByName && opportunity?.followUpSetAt
    ? `Set by ${opportunity.followUpSetByName} on ${formatTimestamp(opportunity.followUpSetAt)}`
    : null;

  const completedDisplay = opportunity?.followUpCompleted && opportunity?.followUpCompletedAt
    ? `Completed on ${formatTimestamp(opportunity.followUpCompletedAt)}`
    : null;

  return (
    <div className="bg-white rounded-lg shadow border-l-4 border-amber-400 p-5 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Re-Engagement Follow-Up
        </h3>
        {completedDisplay ? (
          <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
            ✓ {completedDisplay}
          </span>
        ) : opportunity?.followUpFlag ? (
          <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full font-medium">
            Flagged
          </span>
        ) : null}
      </div>

      {/* Toggle */}
      <label className={`flex items-center gap-3 mb-4 ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}>
        <input
          type="checkbox"
          checked={followUpFlag}
          onChange={(e) => canEdit && setFollowUpFlag(e.target.checked)}
          disabled={!canEdit || saving}
          className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
        />
        <span className="text-sm font-medium text-gray-700">Flag for re-engagement</span>
      </label>

      {followUpFlag && (
        <div className="space-y-4 pl-7">
          {/* Follow-up date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Follow-Up Date
            </label>
            <input
              type="date"
              value={followUpDate}
              onChange={(e) => canEdit && setFollowUpDate(e.target.value)}
              disabled={!canEdit || saving}
              className={`w-48 px-3 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 ${
                canEdit ? 'border-gray-300' : 'border-gray-200 bg-gray-50 cursor-not-allowed'
              }`}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={followUpNotes}
              onChange={(e) => canEdit && setFollowUpNotes(e.target.value)}
              disabled={!canEdit || saving}
              rows={3}
              maxLength={1000}
              placeholder={canEdit ? 'Reason for re-engagement, key details…' : ''}
              className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none ${
                canEdit ? 'border-gray-300' : 'border-gray-200 bg-gray-50 cursor-not-allowed'
              }`}
            />
          </div>
        </div>
      )}

      {/* Audit trail */}
      {setByDisplay && (
        <p className="text-xs text-gray-500 mt-3 pl-7">{setByDisplay}</p>
      )}

      {error && (
        <p className="text-sm text-red-600 mt-2">{error}</p>
      )}

      {/* Actions */}
      {canEdit && (
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 bg-amber-500 text-white text-sm font-medium rounded hover:bg-amber-600 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save Follow-Up'}
          </button>
          {opportunity?.followUpFlag && !opportunity?.followUpCompleted && (
            <button
              onClick={handleMarkComplete}
              disabled={saving}
              className="px-4 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              Mark Complete
            </button>
          )}
        </div>
      )}

      {/* Completion Prompt Modal */}
      {showCompletePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Re-Engagement Complete</h3>
            <p className="text-sm text-gray-600 mb-5">What would you like to do with this opportunity?</p>
            <div className="space-y-2">
              <button
                onClick={() => handleCompleteChoice('qualified')}
                disabled={saving}
                className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded hover:bg-indigo-700 disabled:opacity-50 transition-colors text-left flex items-center gap-2"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Move to Qualified
              </button>
              <button
                onClick={() => handleCompleteChoice('closed-lost')}
                disabled={saving}
                className="w-full px-4 py-2.5 bg-gray-500 text-white text-sm font-medium rounded hover:bg-gray-600 disabled:opacity-50 transition-colors text-left flex items-center gap-2"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Mark Closed Lost with No Follow-Up
              </button>
              <button
                onClick={() => handleCompleteChoice('re-engage')}
                disabled={saving}
                className="w-full px-4 py-2.5 bg-amber-500 text-white text-sm font-medium rounded hover:bg-amber-600 disabled:opacity-50 transition-colors text-left flex items-center gap-2"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                Flag for Re-Engagement Again
              </button>
            </div>
            <button
              onClick={() => setShowCompletePrompt(false)}
              className="mt-3 w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUpCard;
